import { z } from 'zod';
import { shiftDate } from './planning';

/**
 * Zestawienia dla właściciela: sprzedaż, finanse, marża szacunkowa, pulpit.
 * Wszystkie kwoty w groszach. Okres to daty (YYYY-MM-DD) włącznie, w czasie polskim.
 */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');

/** Filtry bez okresu (pulpit liczy własne okresy). Puste parametry adresu traktowane jak brak filtra. */
const opt = <T extends z.ZodTypeAny>(t: T) => z.preprocess((v) => (v === '' ? undefined : v), t.optional());
export const reportScopeSchema = z.object({
  companyId: opt(z.string().uuid()),
  tradeCode: opt(z.string().max(4)),
  ownerId: opt(z.string().uuid()),
});
export type ReportScope = z.infer<typeof reportScopeSchema>;

export const reportFiltersSchema = reportScopeSchema
  .extend({ from: isoDate, to: isoDate })
  .refine((f) => f.from <= f.to, { message: 'Początek okresu musi być przed końcem', path: ['to'] })
  .refine((f) => f.from >= shiftDate(f.to, -3700), { message: 'Okres może mieć najwyżej 10 lat', path: ['from'] });
export type ReportFilters = z.infer<typeof reportFiltersSchema>;

export const REPORT_SECTIONS = { pulpit: 'Pulpit', sprzedaz: 'Sprzedaż', finanse: 'Finanse', marza: 'Marża' } as const;
export type ReportSection = keyof typeof REPORT_SECTIONS;

// ── okresy ──────────────────────────────────────────────────────────────────────

export const PERIOD_PRESETS = {
  ten_miesiac: 'Ten miesiąc',
  poprzedni_miesiac: 'Poprzedni miesiąc',
  ten_kwartal: 'Ten kwartał',
  poprzedni_kwartal: 'Poprzedni kwartał',
  ten_rok: 'Ten rok',
  poprzedni_rok: 'Poprzedni rok',
  ostatnie_12: 'Ostatnie 12 miesięcy',
  wlasny: 'Własny zakres',
} as const;
export type PeriodPreset = keyof typeof PERIOD_PRESETS;

const pad = (n: number) => String(n).padStart(2, '0');
/** Ostatni dzień miesiąca (rok, miesiąc 1–12). */
const monthEnd = (y: number, m: number) => `${y}-${pad(m)}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;
const monthStart = (y: number, m: number) => `${y}-${pad(m)}-01`;

/** Zakres dat dla gotowego okresu, liczony od „dziś” (YYYY-MM-DD). */
export function periodRange(preset: Exclude<PeriodPreset, 'wlasny'>, today: string): { from: string; to: string } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const q = Math.floor((m - 1) / 3);
  switch (preset) {
    case 'ten_miesiac':
      return { from: monthStart(y, m), to: monthEnd(y, m) };
    case 'poprzedni_miesiac': {
      const [py, pm] = m === 1 ? [y - 1, 12] : [y, m - 1];
      return { from: monthStart(py, pm), to: monthEnd(py, pm) };
    }
    case 'ten_kwartal':
      return { from: monthStart(y, q * 3 + 1), to: monthEnd(y, q * 3 + 3) };
    case 'poprzedni_kwartal': {
      const [py, pq] = q === 0 ? [y - 1, 3] : [y, q - 1];
      return { from: monthStart(py, pq * 3 + 1), to: monthEnd(py, pq * 3 + 3) };
    }
    case 'ten_rok':
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'poprzedni_rok':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'ostatnie_12': {
      const [sy, sm] = m === 12 ? [y, 1] : [y - 1, m + 1];
      return { from: monthStart(sy, sm), to: monthEnd(y, m) };
    }
  }
}

/** Okres poprzedni o tej samej długości (do porównań). Pełne miesiące → tyle samo pełnych miesięcy wcześniej. */
export function previousPeriod(from: string, to: string): { from: string; to: string } {
  const fullMonths = from.slice(8) === '01' && to === monthEnd(Number(to.slice(0, 4)), Number(to.slice(5, 7)));
  if (fullMonths) {
    const months = monthsBetween(from, to).length;
    let y = Number(from.slice(0, 4));
    let m = Number(from.slice(5, 7)) - months;
    while (m < 1) {
      m += 12;
      y--;
    }
    const endM = Number(from.slice(5, 7)) - 1;
    const [ey, em] = endM < 1 ? [Number(from.slice(0, 4)) - 1, endM + 12] : [Number(from.slice(0, 4)), endM];
    return { from: monthStart(y, m), to: monthEnd(ey, em) };
  }
  const days = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
  return { from: shiftDate(from, -days), to: shiftDate(from, -1) };
}

/** Miesiące okresu jako „YYYY-MM” (rosnąco). */
export function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  const end = to.slice(0, 7);
  while (`${y}-${pad(m)}` <= end && out.length < 240) {
    out.push(`${y}-${pad(m)}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

const MONTHS_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];
const MONTHS_LONG = ['styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec', 'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'];
/** „2026-10” → „paź 26” (oś wykresu) albo „październik 2026”. */
export function monthLabel(month: string, long = false): string {
  const i = Number(month.slice(5, 7)) - 1;
  return long ? `${MONTHS_LONG[i]} ${month.slice(0, 4)}` : `${MONTHS_SHORT[i]} ${month.slice(2, 4)}`;
}

/** Zmiana procentowa względem poprzedniego okresu; null, gdy brak podstawy. */
export function changePct(current: number, previous: number): number | null {
  if (!previous) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

// ── DTO ─────────────────────────────────────────────────────────────────────────

/** Zakres danych widocznych w zestawieniach (firmy z uprawnienia) i słowniki filtrów. */
export interface ReportMetaDto {
  companies: { id: string; code: string; name: string }[];
  trades: { code: string; name: string; color: string }[];
  owners: { id: string; name: string }[];
  /** marża procentowa (ceny.marza) / kwoty kosztu i marży (ceny.zakup) */
  margin: { percent: boolean; amounts: boolean };
}

export interface SalesKpis {
  /** sprawy (zapytania) utworzone w okresie */
  cases: number;
  /** wydane oferty (wersje) w okresie */
  offersIssued: number;
  /** podpisane umowy (bez aneksów) w okresie */
  contractsSigned: number;
  /** wartość netto podpisanych umów i aneksów w okresie */
  signedNetGr: number;
  /** skuteczność: zakresy z podpisaną umową / zakresy rozstrzygnięte (umowa albo przegrana) spośród utworzonych w okresie */
  conversionPct: number | null;
}

export interface FunnelStageDto {
  stage: 'zapytanie' | 'oferta' | 'akceptacja' | 'umowa';
  label: string;
  /** zakresy (branża × firma w sprawie) utworzone w okresie, które doszły do tego etapu */
  scopes: number;
  cases: number;
}

export interface SalesReportDto {
  kpis: SalesKpis;
  previous: SalesKpis;
  funnel: FunnelStageDto[];
  /** zakresy z okresu: przegrane i nadal otwarte (w toku / wstrzymane) */
  lostScopes: number;
  openScopes: number;
  sources: { source: string; label: string; cases: number; casesSigned: number; signedNetGr: number; conversionPct: number | null }[];
  lostReasons: { reason: string; label: string; scopes: number }[];
  rejections: { reason: string; label: string; offers: number; valueNetGr: number }[];
  owners: { ownerId: string | null; name: string; cases: number; offersIssued: number; contractsSigned: number; signedNetGr: number; avgDaysToSign: number | null; conversionPct: number | null }[];
  trades: { tradeCode: string; name: string; color: string; contracts: number; signedNetGr: number; avgNetGr: number | null }[];
}

export interface FinanceKpis {
  signedNetGr: number;
  signedGrossGr: number;
  paidGr: number;
  invoicedGrossGr: number;
}

export interface FinanceReportDto {
  kpis: FinanceKpis;
  previous: FinanceKpis;
  months: { month: string; signedNetGr: number; signedGrossGr: number; paidGr: number; invoicedGrossGr: number }[];
  byCompany: { companyId: string; name: string; signedNetGr: number; paidGr: number }[];
  byTrade: { tradeCode: string; name: string; color: string; signedNetGr: number }[];
  /** stan na dziś — niezależny od okresu */
  receivables: {
    openGr: number;
    overdueGr: number;
    aging: { bucket: '1-30' | '31-60' | '61-90' | '90+'; label: string; amountGr: number; items: number }[];
    debtors: { partyId: string; name: string; overdueGr: number; openGr: number; contracts: number; oldestDueDate: string | null }[];
    /** prognoza wpływów z harmonogramów: najbliższe 3 miesiące + bez terminu */
    forecast: { month: string; dueGr: number }[];
    noDateGr: number;
  };
  /** faktury wystawione w okresie (zaliczkowe + VAT + końcowe pomniejszone o rozliczone zaliczki + korekty) i ich opłacenie wg Fakturowni */
  invoicing: { connected: boolean; issuedGrossGr: number; paidGrossGr: number; documents: number };
}

export interface MarginRowDto {
  contractId: string;
  number: string;
  kind: 'umowa' | 'aneks';
  caseId: string;
  caseNumber: string;
  client: string;
  companyCode: string;
  ownerName: string;
  signedAt: string;
  netGr: number;
  /** null: brak uprawnienia do kwot albo brak jakiejkolwiek ceny zakupu */
  costGr: number | null;
  marginGr: number | null;
  marginPct: number | null;
  /** pozycje bez ceny zakupu (szacunek niepełny) */
  missingCost: number;
}

export interface MarginGroupDto {
  key: string;
  name: string;
  color?: string;
  netGr: number;
  costGr: number | null;
  marginGr: number | null;
  marginPct: number | null;
  missingCost: number;
}

export interface MarginReportDto {
  amounts: boolean;
  total: MarginGroupDto;
  contracts: MarginRowDto[];
  byTrade: MarginGroupDto[];
  byOwner: MarginGroupDto[];
}

export interface DashboardReportDto {
  /** bieżący miesiąc do dziś i ten sam fragment poprzedniego miesiąca */
  month: { label: string; signedNetGr: number; signedCount: number; paidGr: number };
  previousMonth: { label: string; signedNetGr: number; signedCount: number; paidGr: number };
  overdueGr: number;
  openGr: number;
  /** skuteczność z ostatnich 12 miesięcy */
  conversionPct: number | null;
  /** sprawy otwarte (bez zamkniętych) */
  openCases: number;
  trend: { month: string; signedNetGr: number; signedGrossGr: number; paidGr: number }[];
  margin: { marginPct: number | null; missingCost: number } | null;
}
